import React, { useState, useEffect, useRef } from "react";
import {useStripe, Elements, useElements, PaymentElement} from '@stripe/react-stripe-js';
import { motion, AnimatePresence } from "framer-motion";
import Rollbar from "rollbar";
import { loadStripe } from '@stripe/stripe-js';

var rollbar = new Rollbar({ accessToken: process.env.NEXT_PUBLIC_ROLLBAR_ACCESS_TOKEN });

import {
    H1,
    Box,
    P,
    Btn,
    Grid,
} from '@saladbob/sassafras'

import TournamentCard from "../cards/TournamentCard";
import TeamCard from "../cards/TeamCard";
import CheckoutBtn from "../buttons/CheckoutBtn";
import formatDollars from "../../utils/formatDollars";
import Image from "../media/Image";
import toast from "react-hot-toast";
import BackBtn from "../buttons/BackBtn";
import Spinner from "../spinners/Spinner";
import { useEnv } from "../providers/EnvProvider";
import { useUser } from "../providers/UserProvider";

import { Tournament, TeamDepth } from "../../types";

type Props = {
    tournament: Tournament;
    team: TeamDepth;
    stripeKey?: string;
    paymentIntentID?: string;
    orderMeta?: {
        division?: string;
        optins?: string[];
    };
    onConfirm?: () => void;
    backHandler?: () => void;
}

const CheckoutElements: React.FC<Props> = ({ tournament, orderMeta, team, onConfirm }) => {
    const [checkout, setCheckout] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const stripe = useStripe();
    const elements = useElements();
    const submittingRef = useRef(false);

    const onCheckout = () => {
        setCheckout(true);
    };

    const handleSubmit = async (event) => {
        if (submittingRef.current) {
            return;
        }
        event.preventDefault();
        submittingRef.current = true;
        setSubmitting(true);
        
        if (!stripe || !elements) {
          // Stripe.js hasn't yet loaded.
          // Make sure to disable form submission until Stripe.js has loaded.
          return;
        }

        const {error} = await stripe.confirmPayment({
            //`Elements` instance that was used to create the Payment Element
            elements,
            confirmParams: {
                return_url: `${process.env.NEXT_PUBLIC_URL}/register/success`,
            },
        });

        if (error) {
            rollbar.error(error);
            toast(error.message);
            submittingRef.current = false;
            setSubmitting(false);
        } else {
            // The payment has been processed!
            // For example, the Order status will automatically be set to "paid"
            toast('Payment successful!');
            if (typeof onConfirm === 'function') {
                onConfirm();
            }
        }
        return false;
    };

    return (
        <>
            <Box verticalAlign="center" align="center">
                <H1 font="h2" mb={{ phone: 'sm', laptop: 'lg' }}>Register</H1>
                <Box maxWidth="600px" align="center" verticalAlign="center">
                    <TeamCard team={team} />
                    <H1 font="small" color="neutral300" align="center">
                        .<br/>
                        .<br/>
                    </H1>
                    <P
                        mb="0"
                        font="control"
                        padding={['xs', 'sm']}
                        bgColor="neutral50"
                        width="min-content"
                        rounded="md"
                        nowrap
                    >
                        {formatDollars(tournament.price)} + $10.00 Registraiton Fee
                    </P>
                    <H1 font="small" color="neutral300" align="center">
                        .<br/>
                        .<br/>
                    </H1>
                    <TournamentCard tournament={tournament} imageWidth={100} />
                    <H1 font="small" color="neutral300" align="center">
                        .<br/>
                        .<br/>
                    </H1>
                    <CheckoutBtn
                        price={tournament.price + 10}
                        onClick={onCheckout}
                    >
                        CHECKOUT
                    </CheckoutBtn>
                </Box>
            </Box>
            <AnimatePresence>
                {checkout && (
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ duration: 0.3 }}
                        style={{
                            maxWidth: '100vw',
                            width: '100%',
                            height:'100%',
                            position: 'fixed',
                            overflow: 'auto',
                            right: 0,
                            bottom: 0,
                            zIndex: 100,
                        }}
                    >
                        <Grid columns={{ phone: '1fr', laptop: '1fr 1fr' }} bgColor="neutral50" width="100%" height="100%">
                            <Box padding="lg" verticalAlign="center" align="end">
                                <Box maxWidth="480px" verticalAlign="center">
                                    <BackBtn onClick={() => setCheckout(false)} me="auto" mb="lg"/>
                                    <H1 font="h4" me="auto" mb="0">
                                        Total
                                    </H1>
                                    <H1 font={{ phone: 'h1', laptop: 'jumbo' }} me="auto" color="black" mb="lg">
                                        {formatDollars(tournament.price + 10)}
                                    </H1>
                                    <Grid columns="72px 1fr" gridGap="lg" bgColor="white" mb="md">
                                        <Image image={tournament.image} size="thumbnail" alt={team.name} height={72} width={72} />
                                        <Box verticalAlign="center">
                                            <P font="leader" mb="auto" mt="auto">{tournament.title}</P>
                                        </Box>
                                    </Grid>
                                    <Grid columns="1fr min-content" bdColor="neutral300" bdWidth={[0, 0, 1, 0]} pb="md" mb="md">
                                        <div>
                                            <P
                                                font="control"
                                                mb="0"
                                                padding={['xs', 'sm']}
                                                bgColor="neutral100"
                                                width="min-content"
                                                rounded="md"
                                                nowrap
                                            >
                                                {team.name}
                                            </P>
                                        </div>
                                        <Box align="center">
                                            <P nowrap mb="0">
                                                1x: {formatDollars(tournament.price)}
                                            </P>
                                        </Box>
                                    </Grid>
                                    <Grid columns="1fr min-content" bdColor="neutral300" bdWidth={[0, 0, 1, 0]} pb="md">
                                        <div>
                                            <P
                                                font="control"
                                                mb="0"
                                                nowrap
                                            >
                                                Registration Fees
                                            </P>
                                        </div>
                                        <Box align="end">
                                            <P nowrap mb="0">
                                                1x: {formatDollars(10)}
                                            </P>
                                        </Box>
                                    </Grid>
                                </Box>
                            </Box>
                            <Box padding="lg" verticalAlign="center" bgColor="white" depth="md" align="start">
                                <Box maxWidth="480px">
                                    <form onSubmit={handleSubmit}>
                                        <PaymentElement />
                                        <Btn color="black" full size="lg" mt="lg" type="submit">
                                            {submitting ? <Spinner size={24} /> : 'Pay'}
                                        </Btn>
                                    </form>
                                </Box>
                            </Box>
                        </Grid>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

const CheckOut: React.FC<Props> = ({ tournament, orderMeta, team, stripeKey, backHandler, onConfirm}) => {
    const { user } = useUser();
    const [stripePromise, setStripePromise] = useState(null);
    const [paymentIntentID, setPaymentIntentID] = useState<string>();
    const [options, setOptions] = useState<object>();
    const [errors, setErrors] = useState<string[]>([]);
    const { headerHeight } = useEnv();

    const fetchPaymentIntent = async () => {
        if (!team || !team.players || team.players.length !== 4) {
            toast.error('There was an issue loading your team, please go back and try again.');
            return;
        };
        const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/stripe/payment-intent`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                items: [
                    {
                        tournamentName: tournament.title,
                        teamName: team.name,
                        division: orderMeta.division,
                        optins: orderMeta.optins ? orderMeta.optins.join(', ') : '',
                        playerNames: team.players.map(player => `${player.fname} ${player.lname}`).join(', '),
                        team: team.id,
                        tournament: tournament.id,
                        stripeID: tournament.stripeID,
                        players: team.players.map(player => player.id),
                        qty: 1,
                    },
                    {
                        stripeID: process.env.NEXT_PUBLIC_REGISTRATION_FEE_ID,
                        qty: 1,
                    }
                ]
            }),
        });

        const data = await res.json();

        if (data.error) {
            rollbar.error({
                team,
                error: data.error
            });
            if (data.error.message) {
                toast.error(data.error.message);
                setErrors([data.error.message]);
            } else {
                setErrors([data.error]);
            }
            return;
        }

        setPaymentIntentID(data.id);

        setOptions({
            clientSecret: data.client_secret,
            appearance: {
                theme: 'flat',
            },
        });
    }

    useEffect(() => {
        if (team && team.id) {
            fetchPaymentIntent();
            setStripePromise(loadStripe(stripeKey));
        }
    }, [team]);

    if (errors.length > 0) {
        return (
            <Box mt="xxl" textAlign="center">
                <H1 mb="lg">Looks like there were some issues.</H1>
                {errors.map(error => <P font="leader">{error}</P>)}

                <BackBtn onClick={backHandler} me="auto" mb="lg" />
            </Box>
        );
    }

    if (!options) {
        return (
            <Box minHeight={`calc(100vh - ${headerHeight}px`}verticalAlign="center" align="center">
                <Spinner size={120} />
            </Box>
        );
    }

    return (
        <Elements stripe={stripePromise} options={options}>
            <CheckoutElements
                orderMeta={orderMeta}
                tournament={tournament}
                team={team}
                onConfirm={onConfirm}
                paymentIntentID={paymentIntentID}
            />
        </Elements>
    );
}

export default CheckOut;